import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface OfferRow {
  id: string;
  property_id: string;
  offered_price: number;
  final_sale_price: number | null;
  validity_days: number;
  expires_at: number;
  status:
    | 'draft'
    | 'sent'
    | 'accepted'
    | 'rejected'
    | 'expired'
    | 'signed'
    | 'contract_signed'
    | 'closed'
    // Batch AN — seller pushed back with a Gegenanbot. The original
    // offer parks here until the buyer signs the counter (→ 'accepted'
    // with finalSalePrice from the Gegenanbot) or rejects it.
    | 'counter_offered';
  viewing_date: number | null;
  financing_type: 'without_reservation' | 'with_reservation' | null;
  financing_deadline: number | null;
  vat_applicable: boolean;
  lawyer_id: string | null;
  lawyer_fee_percentage: number | null;
  buyer_commission_type: 'percent' | 'fixed' | null;
  buyer_commission_value: number | null;
  seller_commission_type: 'percent' | 'fixed' | null;
  seller_commission_value: number | null;
  signing_place: string | null;
  buyer_signed_at: number | null;
  seller_signed_at: number | null;
  special_agreements: string | null;
  rejection_reason: string | null;
  notes: string | null;
  sent_note: string | null;
  sent_at: number | null;
  sent_by: string | null;
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface OfferParticipantRow {
  id: string;
  offer_id: string;
  client_id: string;
  participant_role: 'buyer' | 'seller';
  id_copy_received: boolean;
}

export interface OfferAttachmentRow {
  id: string;
  offer_id: string;
  file_key: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: number;
}

export interface OfferDetail {
  offer: OfferRow;
  participants: OfferParticipantRow[];
  attachments: OfferAttachmentRow[];
}

export function useOffers(
  filters: { status?: OfferRow['status']; propertyId?: string; limit?: number } = {},
) {
  return useQuery({
    queryKey: ['offers', filters] as const,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.propertyId) params.set('property_id', filters.propertyId);
      if (filters.limit) params.set('limit', String(filters.limit));
      const res = await apiFetch(`/offers?${params}`);
      if (!res.ok) throw new Error(`offers_list_failed_${res.status}`);
      const json = (await res.json()) as { items: OfferRow[] };
      return json.items;
    },
    staleTime: 30_000,
  });
}

export function useOffer(id: string | undefined) {
  return useQuery({
    queryKey: ['offer', id] as const,
    queryFn: async () => {
      const res = await apiFetch(`/offers/${id}`);
      if (!res.ok) throw new Error(`offer_get_failed_${res.status}`);
      return (await res.json()) as OfferDetail;
    },
    enabled: !!id,
  });
}

export interface CreateOfferPayload {
  propertyId: string;
  offeredPrice: number;
  validityDays?: number;
  vatApplicable?: boolean;
  lawyerId?: string | null;
  lawyerFeePercentage?: number | null;
  buyerCommissionType?: 'percent' | 'fixed' | null;
  buyerCommissionValue?: number | null;
  sellerCommissionType?: 'percent' | 'fixed' | null;
  sellerCommissionValue?: number | null;
  viewingDate?: number | null;
  financingType?: 'without_reservation' | 'with_reservation' | null;
  financingDeadline?: number | null;
  specialAgreements?: string | null;
  notes?: string | null;
  buyerClientIds: string[];
  sellerClientIds: string[];
  /** Admin-only — bypass the "property already notariell verkauft" gate.
   *  Audited server-side. */
  overridePropertySold?: boolean;
}

export function useCreateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOfferPayload) => {
      const res = await apiFetch('/offers', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `offer_create_failed_${res.status}`);
      }
      return (await res.json()) as OfferRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers'] }),
  });
}

export function useUpdateOffer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CreateOfferPayload> & { status?: OfferRow['status']; rejectionReason?: string | null }) => {
      const res = await apiFetch(`/offers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`offer_update_failed_${res.status}`);
      return (await res.json()) as OfferRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      qc.invalidateQueries({ queryKey: ['offer', id] });
    },
  });
}

export function useUploadOfferPdf(offerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { blob: Blob; fileName: string }) => {
      const fd = new FormData();
      fd.append('file', params.blob, params.fileName);
      fd.append('fileName', params.fileName);
      const res = await apiFetch(`/offers/${offerId}/pdf`, {
        method: 'POST',
        body: fd,
        headers: {},
      });
      if (!res.ok) throw new Error(`offer_pdf_upload_failed_${res.status}`);
      return (await res.json()) as OfferAttachmentRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offer', offerId] }),
  });
}
